import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ShieldCheck,
  ShieldAlert,
  Wallet,
  CheckCircle,
  XCircle,
  PauseCircle,
  ArrowUpRight,
  Loader2,
  Lock,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppKit, useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import { useWriteContract, useSwitchChain, useSendTransaction } from 'wagmi';
import { parseUnits } from 'viem';

import '../styles/responsive/pages/PayoutReview.css';
import { fetchWithAuth } from '../utils/api';
import { formatUSD, formatCrypto, truncateHash } from '../utils/formatAmount';
import StatusBadge from '../components/common/StatusBadge';
import Skeleton from '../components/common/Skeleton';
import {
  useGetReviewQuery,
  useClaimMutation,
  useReleaseClaimMutation,
  useAckFlagMutation,
  useApproveMutation,
} from '../store/api/payoutReviewApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const TERMINAL = ['completed', 'failed', 'rejected', 'cancelled', 'canceled'];

// Read a field the API may expose camelCase or snake_case (mirrors the list pages).
const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const SEV_ORDER = { BLOCK: 0, WARN: 1, INFO: 2 };
const sevMeta = {
  BLOCK: { tone: 'block', Icon: ShieldAlert, label: 'Blocking' },
  WARN: { tone: 'warn', Icon: AlertTriangle, label: 'Warnings' },
  INFO: { tone: 'info', Icon: Info, label: 'Info' },
};

// --- Simple reason-required modal (Reject / Hold / Escalate) ---
const ReasonModal = ({ open, title, label, confirmLabel, busy, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open) return null;
  return (
    <>
      <div className="pr-modal-overlay" onClick={() => !busy && onClose()} />
      <div className="pr-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="pr-modal-head">
          <h3>{title}</h3>
          <button className="pr-modal-x" onClick={onClose} disabled={busy} aria-label="Close"><X size={20} /></button>
        </div>
        <div className="pr-modal-body">
          <div className="rd-field">
            <label className="rd-label">{label}</label>
            <textarea
              className="rd-textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </div>
        </div>
        <div className="pr-modal-foot">
          <button className="rd-btn rd-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="rd-btn rd-btn-primary"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || !reason.trim()}
          >
            {busy ? <><Loader2 size={16} className="pr-spin" /> Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
};

const Panel = ({ title, icon, children, className = '' }) => (
  <section className={`pr-panel ${className}`.trim()}>
    <h3 className="pr-panel-title">{icon}{title}</h3>
    {children}
  </section>
);

const Row = ({ label, children, mono }) => (
  <div className="pr-row">
    <span className="pr-row-label">{label}</span>
    <span className={`pr-row-value ${mono ? 'pr-mono' : ''}`.trim()}>{children}</span>
  </div>
);

const PanelSkeleton = ({ lines = 3 }) => (
  <section className="pr-panel">
    <Skeleton variant="text" width={140} />
    <div style={{ marginTop: 12 }}>
      <Skeleton variant="line" count={lines} />
    </div>
  </section>
);

const PayoutReview = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();

  // Wallet hooks (on-chain Approve & release — mirrors PayoutDetail).
  const { open: openWalletModal } = useAppKit();
  const { address: walletAddress, isConnected } = useAppKitAccount();
  const { chainId: currentChainId } = useAppKitNetwork();
  const { switchChainAsync } = useSwitchChain();
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();
  const liveChainIdRef = useRef(currentChainId);
  useEffect(() => { liveChainIdRef.current = currentChainId; }, [currentChainId]);

  const { data: dossier, isLoading, isFetching, error, refetch } = useGetReviewQuery(requestId);
  const [claim] = useClaimMutation();
  const [releaseClaim] = useReleaseClaimMutation();
  const [ackFlag, { isLoading: acking }] = useAckFlagMutation();
  const [approve] = useApproveMutation();

  const [showReject, setShowReject] = useState(false);
  const [showHold, setShowHold] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [loadedSnapshot, setLoadedSnapshot] = useState(null);
  const [stale, setStale] = useState(false);

  // Claim on open; release on unmount. A 409 means another admin holds it.
  useEffect(() => {
    let active = true;
    claim(requestId).unwrap().catch((e) => {
      if (active && e?.status === 409) {
        // lockedBy is also surfaced by the dossier; the banner handles disabling.
      }
    });
    return () => {
      active = false;
      releaseClaim(requestId);
    };
  }, [requestId, claim, releaseClaim]);

  // Snapshot the request/balance version on first load to detect staleness.
  useEffect(() => {
    if (dossier && !loadedSnapshot) {
      setLoadedSnapshot({
        status: dossier.request?.status,
        balance: pick(dossier.balance || {}, 'available', 'availableUsd', 'updatedAt'),
        flagCount: (dossier.flags || []).length,
      });
    }
  }, [dossier, loadedSnapshot]);

  // After a refetch, if the underlying state moved, force an explicit refresh gate.
  useEffect(() => {
    if (!dossier || !loadedSnapshot) return;
    const now = {
      status: dossier.request?.status,
      balance: pick(dossier.balance || {}, 'available', 'availableUsd', 'updatedAt'),
      flagCount: (dossier.flags || []).length,
    };
    if (
      now.status !== loadedSnapshot.status ||
      String(now.balance) !== String(loadedSnapshot.balance)
    ) {
      setStale(true);
    }
  }, [dossier, loadedSnapshot]);

  const handleRefresh = useCallback(() => {
    setStale(false);
    setLoadedSnapshot(null);
    refetch();
  }, [refetch]);

  const request = dossier?.request;
  const merchant = dossier?.merchant;
  const balance = dossier?.balance;
  const destination = dossier?.destination;
  const activity = dossier?.activity;
  const flags = useMemo(() => dossier?.flags || [], [dossier]);
  const lockedBy = dossier?.lockedBy;
  const canApprove = dossier?.canApprove === true;

  const isTerminal = request && TERMINAL.includes(String(request.status).toLowerCase());
  const blockingFlags = flags.filter((f) => f.severity === 'BLOCK');
  const hasUnackedBlock = blockingFlags.length > 0;

  // Group flags by severity for the flag panel.
  const groupedFlags = useMemo(() => {
    const groups = { BLOCK: [], WARN: [], INFO: [] };
    flags.forEach((f) => { (groups[f.severity] || groups.INFO).push(f); });
    return Object.keys(groups)
      .filter((sev) => groups[sev].length)
      .sort((a, b) => SEV_ORDER[a] - SEV_ORDER[b])
      .map((sev) => ({ sev, items: groups[sev] }));
  }, [flags]);

  // Decision bar is disabled until checks resolve AND not stale/locked/terminal.
  const checksResolved = !isLoading && !!dossier;
  const actionsDisabled = busy || !checksResolved || stale || !!lockedBy || isTerminal;

  // --- On-chain sign + approve ---
  const doApprove = async (overrideReason) => {
    if (!request) return;
    const chainId = request.network?.chainId || request.chainId;
    const destAddr = pick(request, 'destinationAddress', 'destination_address') ||
      destination?.address;
    const tokenAmount = pick(request, 'tokenAmount', 'token_amount');

    if (!chainId || !destAddr || !tokenAmount) {
      toast.error('Missing required transaction data');
      return;
    }
    if (!isConnected) {
      openWalletModal();
      return;
    }

    setBusy(true);
    setStep('switching');
    try {
      const requiredChainId = parseInt(chainId, 10);
      let liveChainId = liveChainIdRef.current;
      if (liveChainId !== requiredChainId) {
        try {
          const newChain = await switchChainAsync({ chainId: requiredChainId });
          liveChainId = newChain?.id ?? liveChainIdRef.current;
        } catch (switchErr) {
          console.error('Failed to switch network:', switchErr);
          toast.error('Failed to switch network. Switch manually in your wallet.');
          setBusy(false); setStep('');
          return;
        }
      }
      // Hard gate: never sign on the wrong chain.
      if (liveChainId !== requiredChainId) {
        toast.error('Wallet is on the wrong network — switch and retry');
        setBusy(false); setStep('');
        return;
      }

      const token = request.token || {};
      const tokenDecimals = token.decimals || 18;
      let txHash;
      setStep('signing');
      toast.loading('Sign the transaction in your wallet…', { id: 'pr-sign' });

      let timeoutId;
      const signingTimeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Wallet signing timed out — please try again')), 60000);
      });
      try {
        if (token.isNative || !token.contractAddress) {
          const value = parseUnits(tokenAmount.toString(), tokenDecimals);
          txHash = await Promise.race([
            sendTransactionAsync({ to: destAddr, value }),
            signingTimeout,
          ]);
        } else {
          const erc20Abi = [{
            name: 'transfer', type: 'function',
            inputs: [{ name: 'to', type: 'address' }, { name: 'amount', type: 'uint256' }],
            outputs: [{ name: '', type: 'bool' }],
          }];
          const amount = parseUnits(tokenAmount.toString(), tokenDecimals);
          txHash = await Promise.race([
            writeContractAsync({
              address: token.contractAddress, abi: erc20Abi,
              functionName: 'transfer', args: [destAddr, amount],
            }),
            signingTimeout,
          ]);
        }
      } finally {
        clearTimeout(timeoutId);
      }
      toast.dismiss('pr-sign');

      setStep('completing');
      const result = await approve({ requestId, txHash, overrideReason }).unwrap();
      if (result?.success !== false) {
        toast.success('Transaction submitted successfully');
        navigate('/dashboard/payout-requests');
      } else {
        toast.error(result?.message || 'Approval verification failed');
      }
    } catch (err) {
      toast.dismiss('pr-sign');
      console.error('Approve failed:', err);
      const msg = err?.data?.message || err?.message || 'Approval failed';
      if (msg.includes('user rejected') || msg.includes('User rejected')) {
        toast.error('Transaction cancelled by user');
      } else if (err?.status === 409 || msg.includes('already')) {
        toast.error('This payout changed state — refreshing.');
        handleRefresh();
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
      setStep('');
    }
  };

  // Reject / Hold / Escalate share the existing payout admin endpoints.
  const runAction = async (path, body, successMsg, closeFn) => {
    setBusy(true);
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/payouts/admin/${requestId}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(successMsg);
        closeFn();
        navigate('/dashboard/payout-requests');
      } else {
        toast.error(data.message || 'Action failed');
      }
    } catch (err) {
      console.error(`${path} failed:`, err);
      toast.error('Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAck = async (code) => {
    const reason = window.prompt('Acknowledge this warning — note a reason (logged):');
    if (!reason || !reason.trim()) return;
    try {
      await ackFlag({ requestId, code, ackReason: reason.trim() }).unwrap();
      toast.success('Warning acknowledged');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to acknowledge');
    }
  };

  // ---- Loading (skeleton, decision bar disabled) ----
  if (isLoading) {
    return (
      <div className="ent-page payout-review-page">
        <div className="pr-breadcrumb">
          <Skeleton variant="text" width={200} />
        </div>
        <div className="pr-grid">
          <PanelSkeleton lines={4} />
          <PanelSkeleton lines={3} />
          <PanelSkeleton lines={3} />
          <PanelSkeleton lines={3} />
          <PanelSkeleton lines={4} />
          <PanelSkeleton lines={3} />
        </div>
        <div className="pr-decision-bar">
          <span className="pr-decision-hint"><Loader2 size={15} className="pr-spin" /> Running checks…</span>
        </div>
      </div>
    );
  }

  // ---- Error / not found ----
  if (error || !dossier) {
    const is403 = error?.status === 403;
    return (
      <div className="ent-page payout-review-page">
        <div className="pr-notfound">
          <ShieldAlert size={44} />
          <h2>{is403 ? 'Not authorized' : 'Review unavailable'}</h2>
          <p>{is403 ? 'You need super-admin access to review payouts.' : 'This payout request could not be loaded.'}</p>
          <button className="rd-btn rd-btn-secondary" onClick={() => navigate('/dashboard/payout-requests')}>
            <ChevronLeft size={16} /> Back to payout requests
          </button>
        </div>
      </div>
    );
  }

  const reqId = pick(request, 'requestId', 'request_id', 'id') || requestId;
  const destAddr = pick(request, 'destinationAddress', 'destination_address') || destination?.address;
  const explorerUrl = request.network?.explorerUrl;
  const txHash = pick(request, 'txHash', 'tx_hash');

  return (
    <div className="ent-page payout-review-page">
      {/* Breadcrumb */}
      <div className="pr-breadcrumb">
        <button className="pr-back" onClick={() => navigate('/dashboard/payout-requests')} aria-label="Back">
          <ChevronLeft size={18} />
        </button>
        <span className="pr-crumb-link" onClick={() => navigate('/dashboard/payout-requests')}>Payout requests</span>
        <span className="pr-crumb-sep">/</span>
        <span className="pr-crumb-cur pr-mono">{reqId}</span>
        <span className="pr-crumb-fill" />
        <button className="rd-btn rd-btn-ghost rd-btn-sm" onClick={handleRefresh} disabled={isFetching}>
          <RefreshCw size={14} className={isFetching ? 'pr-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Title */}
      <div className="pr-head">
        <div className="pr-head-main">
          <h1 className="pr-title pr-mono">{reqId}</h1>
          <StatusBadge status={request.status} dot />
        </div>
        <div className="pr-head-sub">
          {canApprove
            ? <span className="pr-verdict ok"><ShieldCheck size={14} /> All checks passed</span>
            : hasUnackedBlock
              ? <span className="pr-verdict block"><ShieldAlert size={14} /> {blockingFlags.length} blocking {blockingFlags.length === 1 ? 'flag' : 'flags'}</span>
              : <span className="pr-verdict warn"><AlertTriangle size={14} /> Review flags before approving</span>}
        </div>
      </div>

      {/* Banners */}
      {lockedBy && (
        <div className="pr-banner block">
          <Lock size={16} />
          <span>Being reviewed by <strong>{lockedBy.name || lockedBy.email || `admin #${lockedBy.id}`}</strong>
            {lockedBy.since ? <> since {fmtDateTime(lockedBy.since)}</> : null}. Actions are disabled for everyone else.</span>
        </div>
      )}
      {stale && !lockedBy && (
        <div className="pr-banner warn">
          <RefreshCw size={16} />
          <span>This request or its balance changed since you opened it. Refresh before taking any action.</span>
          <button className="rd-btn rd-btn-secondary rd-btn-sm" onClick={handleRefresh}>Refresh now</button>
        </div>
      )}
      {isTerminal && (
        <div className="pr-banner info">
          <Info size={16} />
          <span>This request is <strong>{request.status}</strong> — no further action is possible.
            {txHash && explorerUrl && (
              <> Tx <a className="rd-link pr-mono" href={`${explorerUrl}/tx/${txHash}`} target="_blank" rel="noopener noreferrer">{truncateHash(txHash, 8, 6)} <ExternalLink size={11} /></a></>
            )}
            {txHash && !explorerUrl && <> Tx <span className="pr-mono">{truncateHash(txHash, 8, 6)}</span></>}
          </span>
        </div>
      )}

      {/* Six panels */}
      <div className="pr-grid">
        {/* 1. Request */}
        <Panel title="Request" icon={<Wallet size={15} />}>
          <Row label="Amount (USD)" mono>${formatUSD(request.amount)}</Row>
          <Row label="Token amount" mono>
            {pick(request, 'tokenAmount', 'token_amount') != null
              ? `${formatCrypto(parseFloat(pick(request, 'tokenAmount', 'token_amount')))} ${pick(request, 'tokenSymbol', 'token_symbol') || ''}`.trim()
              : '—'}
          </Row>
          <Row label="Network">{pick(request, 'networkName', 'network_name') || request.network?.name || '—'}</Row>
          <Row label="Destination" mono>{destAddr ? truncateHash(destAddr, 8, 8) : '—'}</Row>
          <Row label="Requested">{fmtDateTime(request.createdAt || request.created_at)}</Row>
          <Row label="Request IP" mono>{pick(request, 'requestedFromIp', 'requested_from_ip') || '—'}</Row>
        </Panel>

        {/* 2. Balance & funding */}
        <Panel title="Balance &amp; funding" icon={<Wallet size={15} />}>
          <Row label="Available (settled)" mono>
            {balance?.availableUsd != null || balance?.available != null
              ? `$${formatUSD(balance.availableUsd ?? balance.available)}` : '—'}
          </Row>
          <Row label="Pending / confirming" mono>
            {balance?.pendingUsd != null || balance?.pending != null
              ? `$${formatUSD(balance.pendingUsd ?? balance.pending)}` : '—'}
          </Row>
          <Row label="Covers payout">
            {balance?.coversPayout === false
              ? <span className="pr-chip block">Insufficient</span>
              : balance?.coversPayout === true
                ? <span className="pr-chip ok">Yes</span> : '—'}
          </Row>
          <Row label="Hot wallet / gas" mono>
            {balance?.hotWalletUsd != null ? `$${formatUSD(balance.hotWalletUsd)}` : (balance?.gasOk === false ? 'Shortfall' : '—')}
          </Row>
        </Panel>

        {/* 3. Merchant snapshot */}
        <Panel title="Merchant snapshot" icon={<ShieldCheck size={15} />}>
          <Row label="Merchant">{merchant?.name || '—'}</Row>
          <Row label="Email">{merchant?.email || '—'}</Row>
          <Row label="Status">
            {merchant?.status ? <StatusBadge status={merchant.status} dot /> : '—'}
          </Row>
          <Row label="KYC">{merchant?.kycStatus || merchant?.kyc_status || '—'}</Row>
          <Row label="Member since">{fmtDateTime(merchant?.createdAt || merchant?.created_at)}</Row>
        </Panel>

        {/* 4. Destination intelligence */}
        <Panel title="Destination intelligence" icon={<ShieldCheck size={15} />}>
          <Row label="Address" mono>{destAddr ? truncateHash(destAddr, 10, 8) : '—'}</Row>
          <Row label="Whitelisted">
            {destination?.whitelisted === true ? <span className="pr-chip ok">Yes</span>
              : destination?.whitelisted === false ? <span className="pr-chip warn">No</span> : '—'}
          </Row>
          <Row label="First-time">
            {destination?.firstTime === true ? <span className="pr-chip warn">New destination</span>
              : destination?.firstTime === false ? 'Used before' : '—'}
          </Row>
          <Row label="Last used">{fmtDateTime(destination?.lastUsedAt || destination?.last_used_at)}</Row>
          <Row label="Cooldown until">{fmtDateTime(destination?.cooldownUntil || destination?.cooldown_until)}</Row>
        </Panel>

        {/* 5. Activity & IP trail */}
        <Panel title="Activity &amp; IP trail" icon={<Info size={15} />} className="pr-panel-wide">
          {(activity?.events || []).length ? (
            <ul className="pr-trail">
              {activity.events.map((ev, i) => (
                <li key={i} className="pr-trail-item">
                  <div className="pr-trail-dot" />
                  <div className="pr-trail-body">
                    <span className="pr-trail-action">{ev.action || ev.message}</span>
                    <span className="pr-trail-meta">
                      {fmtDateTime(ev.at || ev.date || ev.createdAt)}
                      {ev.ip && <> · <span className="pr-mono">{ev.ip}</span></>}
                      {ev.geo && <> · {ev.geo}</>}
                      {ev.actor && <> · {ev.actor}</>}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pr-muted">No recorded activity for this request.</p>
          )}
        </Panel>

        {/* 6. Flag panel — grouped by severity */}
        <Panel title="Flags" icon={<ShieldAlert size={15} />} className="pr-panel-wide">
          {flags.length === 0 ? (
            <div className="pr-flags-clear"><ShieldCheck size={18} /> No flags raised.</div>
          ) : (
            groupedFlags.map(({ sev, items }) => {
              const meta = sevMeta[sev] || sevMeta.INFO;
              const { Icon } = meta;
              return (
                <div key={sev} className={`pr-flag-group ${meta.tone}`}>
                  <div className="pr-flag-group-head"><Icon size={14} /> {meta.label} <span className="pr-flag-count">{items.length}</span></div>
                  {items.map((f) => (
                    <div key={f.code} className={`pr-flag ${meta.tone}`}>
                      <div className="pr-flag-main">
                        <span className="pr-flag-code pr-mono">{f.code}</span>
                        <span className="pr-flag-detail">{f.detail}</span>
                      </div>
                      {(f.oldValue != null || f.newValue != null) && (
                        <div className="pr-flag-diff">
                          <span className="pr-mono pr-old">{f.oldValue || '—'}</span>
                          <ArrowUpRight size={12} />
                          <span className="pr-mono pr-new">{f.newValue || '—'}</span>
                        </div>
                      )}
                      {sev === 'WARN' && !isTerminal && !lockedBy && (
                        <button className="rd-btn rd-btn-ghost rd-btn-sm" onClick={() => handleAck(f.code)} disabled={acking}>
                          Acknowledge
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </Panel>
      </div>

      {/* Decision bar */}
      {!isTerminal && (
        <div className="pr-decision-bar">
          <div className="pr-decision-status">
            {!isConnected && (
              <button className="rd-btn rd-btn-ghost rd-btn-sm" onClick={() => openWalletModal()}>
                <Wallet size={14} /> Connect wallet
              </button>
            )}
            {isConnected && (
              <span className="pr-wallet pr-mono"><Wallet size={13} /> {truncateHash(walletAddress, 6, 4)}</span>
            )}
            {busy && step && (
              <span className="pr-decision-hint"><Loader2 size={14} className="pr-spin" />
                {step === 'switching' && ' Switching network…'}
                {step === 'signing' && ' Sign in wallet…'}
                {step === 'completing' && ' Completing…'}
              </span>
            )}
          </div>
          <div className="pr-decision-actions">
            <button className="rd-btn rd-btn-secondary" onClick={() => setShowEscalate(true)} disabled={actionsDisabled}>
              <ArrowUpRight size={16} /> Escalate
            </button>
            <button className="rd-btn rd-btn-secondary" onClick={() => setShowHold(true)} disabled={actionsDisabled}>
              <PauseCircle size={16} /> Hold
            </button>
            <button className="rd-btn rd-btn-secondary pr-btn-reject" onClick={() => setShowReject(true)} disabled={actionsDisabled}>
              <XCircle size={16} /> Reject
            </button>
            <button
              className="rd-btn rd-btn-primary"
              onClick={() => doApprove(hasUnackedBlock ? window.prompt('Override reason (required to override blocking flags, logged):')?.trim() || undefined : undefined)}
              disabled={actionsDisabled || (!canApprove && !hasUnackedBlock)}
              title={hasUnackedBlock ? 'Blocking flags present — approval requires an override reason + second admin' : undefined}
            >
              <CheckCircle size={16} /> Approve &amp; release
            </button>
          </div>
          {hasUnackedBlock && !lockedBy && !stale && (
            <div className="pr-decision-block-note">
              <ShieldAlert size={13} /> Approval blocked: {blockingFlags.map((f) => f.code).join(', ')}. Overriding requires a reason and a second super-admin.
            </div>
          )}
        </div>
      )}

      <ReasonModal
        open={showReject}
        title="Reject payout request"
        label="Rejection reason (shown to merchant)"
        confirmLabel="Reject request"
        busy={busy}
        onClose={() => setShowReject(false)}
        onConfirm={(reason) => runAction('reject', { reason }, 'Payout request rejected', () => setShowReject(false))}
      />
      <ReasonModal
        open={showHold}
        title="Hold payout request"
        label="Hold reason"
        confirmLabel="Place on hold"
        busy={busy}
        onClose={() => setShowHold(false)}
        onConfirm={(reason) => runAction('hold', { reason }, 'Payout placed on hold', () => setShowHold(false))}
      />
      <ReasonModal
        open={showEscalate}
        title="Escalate payout request"
        label="Escalation note"
        confirmLabel="Escalate"
        busy={busy}
        onClose={() => setShowEscalate(false)}
        onConfirm={(reason) => runAction('escalate', { reason }, 'Payout escalated', () => setShowEscalate(false))}
      />
    </div>
  );
};

export default PayoutReview;
